import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Settings, Mail, CheckCircle2, XCircle, AlertCircle, Clock, Bell } from "lucide-react";

import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";
import { logoutUser } from "../../actions/authActions";
import { toggleDarkMode } from "../../actions/preferenceActions";
import AdminCommandPalette from "./AdminCommandPalette";
import AdminNotifications from "./AdminNotifications";

import { buildAdminBreadcrumb } from "../../utils/adminBreadcrumb";
import {
  AdminAppShell,
  PageHeader,
  Button,
  SegmentedControl,
  IconButton,
  Tooltip,
  Badge,
  Breadcrumb,
  DataTable,
  Stat,
  Select,
  FormField,
  SearchInput,
} from "../../design-system";

const STATUS_MAP = {
  SENT:    { variant: "success", label: "Envoyé",      icon: CheckCircle2 },
  FAILED:  { variant: "danger",  label: "Échec",       icon: XCircle },
  BLOCKED: { variant: "warn",    label: "Bloqué",      icon: AlertCircle },
  PENDING: { variant: "neutral", label: "En attente",  icon: Clock },
};

class ContactsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      contacts: [],
      stats: { total: 0, sent: 0, failed: 0, blocked: 0, pending: 0 },
      loading: true,
      filters: { status: "" },
    };
  }

  componentDidMount() {
    this.loadContacts();
    this.loadStats();
  }

  loadContacts = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const params = new URLSearchParams();
      if (this.state.filters.status) params.append("status", this.state.filters.status);
      const response = await axios.get(`/api/contacts?${params.toString()}`);
      this.setState({ contacts: response.data.contacts || [], loading: false });
    } catch (error) {
      console.error("Error loading contacts:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger les contacts",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  loadStats = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get(`/api/contacts/stats`);
      this.setState({
        stats: response.data.stats || { total: 0, sent: 0, failed: 0, blocked: 0, pending: 0 },
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  handleFilterChange = (e) => {
    this.setState(
      { filters: { ...this.state.filters, status: e.target.value } },
      this.loadContacts,
    );
  };

  onLogout = () => {
    confirmAlert({
      title: "Déconnexion",
      message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      type: "warning",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      onConfirm: () => {
        this.props.logoutUser();
        this.props.history.push("/login");
      },
    });
  };

  onToggleDarkMode = () => {
    const { _preferences } = this.props;
    this.props.toggleDarkMode(!_preferences?._dark);
  };

  buildColumns() {
    return [
      {
        key: "name", header: "Nom", sortable: true,
        render: (row) => (
          <Link to={`/dashboard/contacts/${row.id}`} className="font-semibold text-text hover:underline underline-offset-2">
            {row.name}
          </Link>
        ),
      },
      {
        key: "email", header: "Email",
        render: (row) => <span className="text-sm text-text-2">{row.email}</span>,
      },
      {
        key: "subject", header: "Sujet",
        render: (row) => <span className="text-sm text-text-2 line-clamp-1">{row.subject || "—"}</span>,
      },
      {
        key: "status", header: "Statut", width: 130,
        render: (row) => {
          const s = STATUS_MAP[row.status] || STATUS_MAP.PENDING;
          const Icon = s.icon;
          return (
            <Badge variant={s.variant} leadingIcon={<Icon className="w-3 h-3" strokeWidth={2} />}>
              {s.label}
            </Badge>
          );
        },
      },
      {
        key: "createdAt", header: "Date", sortable: true, width: 160,
        render: (row) => (
          <span className="font-mono text-xs text-mute">
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString("fr-FR", {
              year: "numeric", month: "2-digit", day: "2-digit",
              hour: "2-digit", minute: "2-digit",
            }) : "—"}
          </span>
        ),
      },
      {
        key: "recaptchaScore", header: "Score reCAPTCHA", align: "right", width: 130,
        render: (row) => (
          <span className="font-mono tabular-nums text-sm text-text-2">
            {row.recaptchaScore != null ? row.recaptchaScore.toFixed(2) : "—"}
          </span>
        ),
      },
    ];
  }

  render() {
    const { contacts, stats, loading, filters } = this.state;
    const { user, _preferences, location, history } = this.props;

    return (
      <AdminAppShell
        user={user}
        currentPath={location.pathname}
        isDarkMode={_preferences?._dark || false}
        onToggleDarkMode={this.onToggleDarkMode}
        onLogout={this.onLogout}
        LinkComponent={Link}
        pageTitle="Contacts"
        breadcrumb={<Breadcrumb items={buildAdminBreadcrumb(location.pathname)} LinkComponent={Link} />}
        counts={{ contacts: stats.pending }}
        topbarCenter={<AdminCommandPalette />}
        topbarRight={<AdminNotifications />}
      >
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section vi · Contacts"
            title={<>Tout ce qui arrive <span className="font-serif italic font-normal align-baseline">par la boîte.</span></>}
            description="Tous les messages reçus via le formulaire de contact du site, classés par statut."
            actions={
              <Button
                as={Link}
                to="/dashboard/contacts/settings"
                variant="secondary"
                leftIcon={<Settings className="w-4 h-4" strokeWidth={1.8} />}
              >
                Paramètres
              </Button>
            }
          />

          <Stat.Group>
            <Stat variant="ink"   label="Total"      value={String(stats.total)}   icon={<Mail className="w-4 h-4" strokeWidth={1.8} />} />
            <Stat variant="cream" label="Envoyés"    value={String(stats.sent)}    icon={<CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />} />
            <Stat variant="cream" label="En attente" value={String(stats.pending)} icon={<Clock className="w-4 h-4" strokeWidth={1.8} />} />
            <Stat variant="ink"   label="Bloqués"    value={String(stats.blocked)} icon={<AlertCircle className="w-4 h-4" strokeWidth={1.8} />} />
          </Stat.Group>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <SegmentedControl
              value={filters.status || "ALL"}
              onChange={(v) => this.handleFilterChange({ target: { value: v === "ALL" ? "" : v } })}
              options={[
                { value: "ALL",     label: "Tous",        count: stats.total },
                { value: "SENT",    label: "Envoyés",     count: stats.sent },
                { value: "PENDING", label: "En attente",  count: stats.pending },
                { value: "FAILED",  label: "Échecs" },
                { value: "BLOCKED", label: "Bloqués",     count: stats.blocked },
              ]}
              ariaLabel="Filtrer par statut"
            />
            <div className="text-xs text-mute font-mono">{contacts.length} affiché(s)</div>
          </div>

          <DataTable
            data={contacts}
            columns={this.buildColumns()}
            rowKey="id"
            isLoading={loading}
            variant="duotone"
            showRowIndex
            onRowClick={(row) => history.push(`/dashboard/contacts/${row.id}`)}
          />
        </div>
      </AdminAppShell>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  _preferences: state._preferences,
});

export default connect(mapStateToProps, { logoutUser, toggleDarkMode })(withRouter(ContactsList));
